// Summary tiles (Active · Due soon · Overdue · Stale · Awaiting review) — one
// StatCard per count, shared by My / Team / Departments and the drill-down.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarClock, ClipboardList, Eye, Hourglass } from 'lucide-react';
import { StatCard } from '@/features/shared/components/StatCard';
import type { StaleBasis, WorkloadTotals } from '@/services/workloadService';

export interface SummaryTilesProps {
  totals: Partial<WorkloadTotals> | null | undefined;
  loading?: boolean;
  /** Stale threshold shown under the Stale tile ("no activity for N calendar/business days"). */
  staleAfterDays?: number;
  /** Policy `stale_basis`; default calendar days. */
  staleBasis?: StaleBasis;
  dueSoonDays?: number;
  compact?: boolean;
}

/** The stale hint, switching on the policy's `stale_basis`. */
export const staleHint = (t: (key: string, opts?: Record<string, unknown>) => string, days: number, basis: StaleBasis | undefined): string => (
  basis === 'business_days'
    ? t('tile_stale_hint_business', { defaultValue: 'no activity for {{count}} business days', count: days })
    : t('tile_stale_hint', { defaultValue: 'no activity for {{count}} calendar days', count: days })
);

/** The stale rule sentence (My workload footer), switching on `stale_basis`. */
export const staleRule = (t: (key: string, opts?: Record<string, unknown>) => string, days: number, basis: StaleBasis | undefined): string => (
  basis === 'business_days'
    ? t('stale_rule_business', { defaultValue: 'A work item counts as stale after {{count}} business days without meaningful activity.', count: days })
    : t('stale_rule', { defaultValue: 'A work item counts as stale after {{count}} calendar days without meaningful activity.', count: days })
);

export const SummaryTiles: React.FC<SummaryTilesProps> = ({ totals, loading, staleAfterDays, staleBasis, dueSoonDays, compact }) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
  const n = (v?: number) => String(v ?? 0);
  const tiles = [
    { key: 'active', icon: ClipboardList, tone: 'default' as const, label: t('tile_active', { defaultValue: 'Active' }), value: n(totals?.active) },
    { key: 'due_soon', icon: CalendarClock, tone: 'info' as const, label: t('tile_due_soon', { defaultValue: 'Due soon' }), value: n(totals?.due_soon),
      hint: dueSoonDays ? t('tile_due_soon_hint', { defaultValue: 'within {{count}} days', count: dueSoonDays }) as string : undefined },
    { key: 'overdue', icon: AlertTriangle, tone: 'danger' as const, label: t('tile_overdue', { defaultValue: 'Overdue' }), value: n(totals?.overdue) },
    { key: 'stale', icon: Hourglass, tone: 'warning' as const, label: t('tile_stale', { defaultValue: 'Stale' }), value: n(totals?.stale),
      hint: staleAfterDays ? staleHint(tt, staleAfterDays, staleBasis) : undefined },
    { key: 'awaiting_review', icon: Eye, tone: 'success' as const, label: t('tile_awaiting_review', { defaultValue: 'Awaiting review' }), value: n(totals?.awaiting_review) },
  ];
  return (
    <div className={`grid gap-3 ${compact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 md:grid-cols-5'}`} data-testid="workload-tiles">
      {tiles.map((x) => (
        <div key={x.key} data-testid={`tile-${x.key}`}>
          <StatCard icon={x.icon} tone={x.tone} label={x.label as string} value={x.value} hint={x.hint} loading={!!loading} />
        </div>
      ))}
    </div>
  );
};

export default SummaryTiles;
